package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.content
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.Role
import dev.lingvo.site.ds.HIT_AREA_FLOOR_PX
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.AppNav
import dev.lingvo.site.nav.NavGlyph
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The settings hub, which is both a destination in this row and the prefix of every other one. */
private const val SETTINGS_HUB_PATH = "/app/settings"

/**
 * The sibling pages of wherever you are.
 *
 * **Seventeen pages in this app had no inbound link from anywhere.** Billing had children —
 * plans, invoices, seats, the payment-fix flow — reachable only by typing their URLs; so did usage,
 * account, and half of every project. `AppNav.settingsFor(role)` existed to answer this and had
 * zero call sites: the destination list was written, and nothing ever rendered it.
 *
 * A row of links, not a re-implementation of the sidebar. It is the same list the sidebar filters
 * by role, drawn one level down, so a page cannot appear here that the server would refuse.
 */
@Composable
fun SettingsNav(currentPath: String) {
    // **The role in *this* workspace, and nothing else.** `strongestRole()` is the best role held
    // anywhere, so an owner of their own workspace who is a translator in a client's was offered
    // Billing, Providers and the audit log while looking at the client's — every one of which the
    // server then refuses, because it asks the narrower question. Null draws nothing here rather
    // than guessing: every entry in this list has a management page behind it.
    val role = WorkspaceSelection.current()?.role
    val group = AppNav.settingsGroupFor(role, currentPath) ?: return
    SectionNav(
        // **Translated here, keyed by id, exactly as the rail does it** (`AppShell.kt`). These
        // destinations are class-init `val`s in the nav model, so a lookup written there would
        // freeze in English for the life of the page — and taking `label` raw, as this row did,
        // freezes it just as hard. `extract-strings.mjs` mints `nav.<id>` for every one of them, so
        // the settings pills were the only place in the shell rendering keys that already existed.
        //
        // The marks come from the model too, so a pill and the rail row for the same destination
        // cannot drift apart — which is the whole reason the glyph stopped being a `when` in the
        // shell that only the shell could read.
        destinations = listOf(
            SectionNavItem(
                t("section_nav.all_settings", "All settings"),
                SETTINGS_HUB_PATH,
                NavGlyph.Settings,
            ),
        ) + group.second.map { SectionNavItem(t("nav.${it.catalogueKey}", it.label), it.path, it.glyph) },
        currentPath = currentPath,
        ariaLabel = settingsNavLandmark(group.first),
        isActive = { destination ->
            val normalized = currentPath.substringBefore('?').substringBefore('#').removeSuffix("/")
            val path = destination.removeSuffix("/")
            // **The hub is not a parent for this purpose.** Every settings route starts with
            // `/app/settings/`, so the prefix rule made "All settings" current on all seven of
            // them — two pills carrying `accentSubtle`, the accent word, the outline *and*
            // `aria-current="page"` at once, on a row whose whole job is saying where you are.
            // The prefix rule still earns its place for the others: `/app/settings/providers/deepl`
            // must light the Providers pill.
            if (path == SETTINGS_HUB_PATH) {
                normalized == path
            } else {
                normalized == path || normalized.startsWith("$path/")
            }
        },
    )
}

/**
 * What a screen reader calls this pill row.
 *
 * **The landmark is translated too, and by the key every other surface already uses.**
 * `AppNav.settingsGroupFor` answers with the model's English group label — the lists are
 * class-init `val`s, so they cannot hold a lookup — and this was the fourth surface to draw one.
 * The rail (`AppShell.kt`), the hub (`settings/Index.kt`) and the pills inside this very row all
 * ask [AppNav.navGroupKey]; writing the label raw announced "Settings: Money" in English around a
 * row whose every pill was in the reader's own language, with `nav.group.money` sitting unread in
 * the catalogue.
 *
 * Both halves go through the catalogue: the frame, because "Settings: {group}" is not word order
 * every language keeps, and the group, because it is a word somebody has already translated.
 */
@Composable
internal fun settingsNavLandmark(groupLabel: String): String = tf(
    "section_nav.settings_group_landmark",
    "Settings: {group}",
    "group" to t(AppNav.navGroupKey(groupLabel), groupLabel),
)

/** One pill: what it says, where it goes, and the mark it carries. */
data class SectionNavItem(val label: String, val path: String, val glyph: NavGlyph)

/**
 * Which role decides what the pill row offers.
 *
 * Prefers the project's own org over the selected workspace, once it is known — the header button
 * beside this row (`Index.kt`) gates Pre-translate on `Session.roleInOrg(project.orgId)`, and this
 * row must ask the same question. `WorkspaceSelection.current()` answers "the role in whichever
 * workspace was last selected", which is the wrong question the moment someone opens a project in
 * an org other than the one they last picked: an OWNER of their own workspace who is a TRANSLATOR
 * in a client's, viewing the client's project, was offered Settings and Data the server then
 * refused.
 *
 * Falls back to the selected workspace's role only while the project's org is still unresolved —
 * the directory has not loaded yet — matching the loading behaviour every other role-gated surface
 * already has, rather than drawing nothing for however long that takes.
 */
fun projectNavRole(projectOrgId: String?, roleInProjectOrg: Role?, selectedWorkspaceRole: Role?): Role? =
    if (projectOrgId != null) roleInProjectOrg else selectedWorkspaceRole

/**
 * A row of pills, drawn from the nav model.
 *
 * `ProjectNav` used to live beside this and rendered the project sections as a second pill row. The
 * rail absorbed those sections when a project became somewhere you *enter* rather than a folder you
 * view from outside, and the row was left behind with no call sites — two lists of the same
 * destinations, one above the other, is the duplication the project overview spent four commits
 * deleting. It is deleted here; [projectNavRole], which answers a question the project header still
 * asks, stays.
 */
@Composable
private fun SectionNav(
    destinations: List<SectionNavItem>,
    currentPath: String,
    ariaLabel: String,
    isActive: (String) -> Boolean = { path -> currentPath == path },
) {
    if (destinations.isEmpty()) return
    val palette = ColorMode.current.toSitePalette()
    Nav(SectionNavStyle.toModifier().toAttrs { attr("aria-label", ariaLabel) }) {
        Row(
            Modifier.gap(Space.XXS).styleModifier {
                property("flex-wrap", "wrap")
            },
        ) {
            destinations.forEach { (label, path, glyph) ->
                val active = isActive(path)
                Link(
                    path = path,
                    modifier = SectionNavItemStyle.toModifier()
                        .then(
                            if (active) {
                                // The underline carries selection without relying on colour.
                                Modifier
                                    .backgroundColor(palette.accentSubtle)
                                    .color(palette.accent)
                                    .styleModifier {
                                        property("text-decoration", "underline")
                                        property("text-underline-offset", "4px")
                                    }
                            } else {
                                Modifier
                            },
                        )
                        .then(if (active) Modifier.attr("aria-current", "page") else Modifier),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) {
                    // Decorative: the label beside it says the same thing, so a screen reader that
                    // announced both would read every pill twice.
                    Span(Modifier.attr("aria-hidden", "true").toAttrs()) { NavIcon(glyph) }
                    Text(label)
                }
            }
        }
    }
}

/**
 * The row's own separation from the page it sits above.
 *
 * `Space.SM` put the pills eight pixels under a 44px display title, on a header whose title-to-
 * subtitle gap is four and whose gap to its own content is thirty-two — so the chrome was closer to
 * the identity than the identity was to the thing it names, and the row read as part of the title
 * block. `Space.XXXL` is the header's own rung: navigation now separates from the page the way the
 * page separates from its header.
 */
val SectionNavStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(bottom = Space.XXXL)
}

val SectionNavItemStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .height(32.px)
            .padding(leftRight = Space.MD)
            .gap(Space.XS)
            .borderRadius(Radius.Pill)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .color(colorMode.toSitePalette().textSecondary)
            .transitionColors(Motion.FastMs)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
            }
    }
    // **32 visual, 40 to the pointer.** The pill was ~32 tall at 6px padding, below the 40px floor,
    // and a section nav is exactly the control somebody reaches for on a trackpad while reading —
    // a target that small is one somebody misses and then blames themselves for.
    cssRule("::after") {
        Modifier
            .content("")
            .position(Position.Absolute)
            .styleModifier {
                property("top", "50%")
                property("left", "0")
                property("width", "100%")
                property("height", "${HIT_AREA_FLOOR_PX}px")
                property("transform", "translateY(-50%)")
            }
    }
    hover {
        // The paper's second step. `surfaceRaised` is pure white and belongs to things that float.
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
    }
    focusVisible {
        Modifier.styleModifier {
            property("outline", "none")
            property("text-decoration", "underline")
            property("text-decoration-thickness", "2px")
            property("text-underline-offset", "4px")
        }
    }
}

